import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchEnterpriseProfile, updateEnterpriseProfile, EnterpriseOnboardingPayload } from '../../../api/enterpriseApi';
import styles from './EnterpriseOnboardingPage.module.css';
import { JurisdictionPicker } from '../components/JurisdictionPicker';

interface TagInputProps {
  label: string;
  value: string;
  tags: string[];
  onChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}
function TagInput({ label, value, tags, onChange, onAdd, onRemove }: TagInputProps) {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <div className={styles.tagInputRow}>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
          placeholder="Type and press Enter"
        />
        <button type="button" className={styles.addBtn} onClick={onAdd}>Add</button>
      </div>
      <div className={styles.tags}>
        {tags.map((t, i) => (
          <span key={i} className={styles.tag}>
            {t}
            <button type="button" onClick={() => onRemove(i)}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function EnterpriseProfileEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    company_name: '',
    jurisdiction: '',
    years_in_operation: '',
    mac_id: '',
    gst_number: '',
    founder_names: [] as string[],
    num_members: '',
    existing_policies: [] as string[],
    products_services: '',
    other_jurisdictions: [] as string[],
    new_jurisdictions: [] as string[],
    founderInput: '',
    policyInput: '',
  });

  useEffect(() => {
    fetchEnterpriseProfile()
      .then(p => setForm(prev => ({
        ...prev,
        company_name: p.company_name ?? '',
        jurisdiction: p.jurisdiction ?? '',
        years_in_operation: p.years_in_operation ?? '',
        mac_id: p.mac_id ?? '',
        gst_number: p.gst_number ?? '',
        founder_names: p.founder_names ?? [],
        num_members: p.num_members ?? '',
        existing_policies: p.existing_policies ?? [],
        products_services: p.products_services ?? '',
        other_jurisdictions: p.other_jurisdictions ?? [],
        new_jurisdictions: p.new_jurisdictions ?? [],
      })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key: string, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const addTag = (listKey: string, inputKey: string) => {
    const val = (form as any)[inputKey]?.trim();
    if (!val) return;
    const existing = (form as any)[listKey] as string[] ?? [];
    if (!existing.includes(val)) set(listKey, [...existing, val]);
    set(inputKey, '');
  };

  const removeTag = (listKey: string, idx: number) => {
    const existing = (form as any)[listKey] as string[];
    set(listKey, existing.filter((_: string, i: number) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload: EnterpriseOnboardingPayload = {
        company_name: form.company_name,
        jurisdiction: form.jurisdiction,
        years_in_operation: form.years_in_operation,
        mac_id: form.mac_id || undefined,
        gst_number: form.gst_number || undefined,
        founder_names: form.founder_names,
        num_members: form.num_members || undefined,
        existing_policies: form.existing_policies,
        products_services: form.products_services || undefined,
        other_jurisdictions: form.other_jurisdictions,
        new_jurisdictions: form.new_jurisdictions,
      };
      await updateEnterpriseProfile(payload);
      navigate('/enterprise/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to update. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 40 }}>Loading…</div>;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1>Edit Enterprise Profile</h1>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={styles.body}>
            <div className={styles.field}>
              <label>Company Name <span className={styles.req}>*</span></label>
              <input value={form.company_name} onChange={e => set('company_name', e.target.value)} required />
            </div>
            <div className={styles.field}>
              <label>Primary Jurisdiction <span className={styles.req}>*</span></label>
              <input value={form.jurisdiction} onChange={e => set('jurisdiction', e.target.value)} required />
            </div>
            <div className={styles.field}>
              <label>Years in Operation <span className={styles.req}>*</span></label>
              <input value={form.years_in_operation} onChange={e => set('years_in_operation', e.target.value)} required type="number" min="0" />
            </div>
            <div className={styles.field}>
              <label>MAC ID</label>
              <input value={form.mac_id} onChange={e => set('mac_id', e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>GST Number</label>
              <input value={form.gst_number} onChange={e => set('gst_number', e.target.value)} />
            </div>
            <TagInput
              label="Founder Names"
              value={form.founderInput}
              tags={form.founder_names}
              onChange={v => set('founderInput', v)}
              onAdd={() => addTag('founder_names', 'founderInput')}
              onRemove={i => removeTag('founder_names', i)}
            />
            <div className={styles.field}>
              <label>Number of Members</label>
              <input value={form.num_members} onChange={e => set('num_members', e.target.value)} type="number" min="1" />
            </div>
            <TagInput
              label="Existing Policies"
              value={form.policyInput}
              tags={form.existing_policies}
              onChange={v => set('policyInput', v)}
              onAdd={() => addTag('existing_policies', 'policyInput')}
              onRemove={i => removeTag('existing_policies', i)}
            />
            <div className={styles.field}>
              <label>Products & Services</label>
              <textarea value={form.products_services} onChange={e => set('products_services', e.target.value)} rows={3} />
            </div>
            {/* Picked from the corpus (country, then jurisdiction), so the saved names match what the chat holds. */}
            <JurisdictionPicker
              label="Other Jurisdictions (existing)"
              tags={form.other_jurisdictions}
              onAdd={v => set('other_jurisdictions', [...form.other_jurisdictions, v])}
              onRemove={i => set('other_jurisdictions', form.other_jurisdictions.filter((_, idx) => idx !== i))}
            />
            <JurisdictionPicker
              label="New Jurisdictions to Establish In"
              tags={form.new_jurisdictions}
              onAdd={v => set('new_jurisdictions', [...form.new_jurisdictions, v])}
              onRemove={i => set('new_jurisdictions', form.new_jurisdictions.filter((_, idx) => idx !== i))}
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={styles.backBtn} onClick={() => navigate('/enterprise/dashboard')}>Cancel</button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
